import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { TrendingUp, BarChart3, Calendar, Trophy, Upload, ArrowRight, Clock, Sparkles, Loader2, Mail, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import VerifyEmailModal from "@/components/VerifyEmailModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [stats, setStats] = useState(null);
  const [analyses, setAnalyses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPlans, setShowPlans] = useState(searchParams.get("showPlans") === "true");
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [planBilling, setPlanBilling] = useState("monthly");
  const [planCurrency, setPlanCurrency] = useState("USD");

  const plans = [
    { name: "Starter", key: "starter", monthlyUSD: 39, yearlyUSD: 374, monthlyMXN: 699, yearlyMXN: 6710, analyses: "5 / month", features: ["Full Neuro Score", "Attention Heatmap", "AI Recommendations"], icon: "🎯" },
    { name: "Growth", key: "growth", monthlyUSD: 89, yearlyUSD: 854, monthlyMXN: 1595, yearlyMXN: 15312, analyses: "20 / month", features: ["Everything in Starter", "PDF Reports", "Shareable Links"], icon: "🚀", popular: true },
    { name: "Agency", key: "agency", monthlyUSD: 219, yearlyUSD: 2102, monthlyMXN: 3925, yearlyMXN: 37680, analyses: "60 / month", features: ["Everything in Growth", "Team Collaboration", "Priority Support"], icon: "⚡" },
  ];

  const getPlanPrice = (p) => {
    if (planCurrency === "MXN") return planBilling === "yearly" ? p.yearlyMXN : p.monthlyMXN;
    return planBilling === "yearly" ? p.yearlyUSD : p.monthlyUSD;
  };

  const [verifyModal, setVerifyModal] = useState(false);

  const handleSelectPlan = async (planKey) => {
    setCheckoutLoading(planKey);
    try {
      const { data } = await axios.post(`${API}/stripe/checkout`, {
        plan: planKey, billing: planBilling, currency: planCurrency.toLowerCase()
      }, { withCredentials: true });
      window.location.href = data.url;
    } catch (err) {
      const detail = err.response?.data?.detail || "Something went wrong";
      if (err.response?.status === 403 && detail.toLowerCase().includes("verify")) {
        setVerifyModal(true);
      } else {
        alert(detail);
      }
      setCheckoutLoading(null);
    }
  };

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, analysesRes] = await Promise.all([
        axios.get(`${API}/analyses/stats`, { withCredentials: true }),
        axios.get(`${API}/analyses`, { withCredentials: true }),
      ]);
      setStats(statsRes.data);
      setAnalyses(analysesRes.data);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const greeting = () => {
    const h = new Date().getHours();
    if (h < 12) return "Good morning";
    if (h < 17) return "Good afternoon";
    return "Good evening";
  };

  const scoreColor = (s) => {
    if (s >= 80) return "text-emerald-600";
    if (s >= 50) return "text-amber-600";
    return "text-red-500";
  };

  const statusBadge = (status) => {
    const map = {
      completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
      processing: "bg-amber-50 text-amber-700 border-amber-200",
      failed: "bg-red-50 text-red-700 border-red-200",
    };
    return map[status] || map.processing;
  };

  if (loading) {
    return (
      <div data-testid="dashboard-loading" className="space-y-6">
        <div className="h-8 w-64 bg-slate-200 rounded-lg animate-pulse" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1,2,3,4].map(i => <div key={i} className="h-28 bg-white rounded-2xl animate-pulse" />)}
        </div>
        <div className="h-48 bg-white rounded-2xl animate-pulse" />
      </div>
    );
  }

  return (
    <div data-testid="dashboard-page" className="space-y-6 max-w-6xl">
      {/* Greeting */}
      <div>
        <h1 className="text-2xl font-bold" style={{ fontFamily: "Outfit, sans-serif", color: "#0F172A" }}>
          {greeting()}, {user?.name?.split(" ")[0] || "there"}.
        </h1>
        <p className="text-sm text-slate-500 mt-1">Ready to optimize your ads?</p>
      </div>

      {/* Free plan banner */}
      {(user?.plan === "free" || user?.plan === "trial" || !user?.plan) && (
        <div className="bg-gradient-to-r from-orange-500 to-fuchsia-500 rounded-2xl p-5 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
              <Sparkles size={20} className="text-white" />
            </div>
            <div>
              <p className="text-sm font-bold text-white">{user?.plan === "trial" ? "Upgrade your plan for more analyses" : "Choose a plan to start analyzing ads"}</p>
              <p className="text-xs text-white/80">{user?.plan === "trial" ? "You have 2 free analyses per month. Upgrade for more." : "Choose a plan to start analyzing ads."}</p>
            </div>
          </div>
          <Button onClick={() => setShowPlans(true)} className="bg-white text-orange-600 hover:bg-orange-50 rounded-xl text-sm font-semibold px-5 h-10 flex-shrink-0">
            View Plans <ArrowRight size={14} className="ml-1" />
          </Button>
        </div>
      )}

      {/* Plans modal */}
      {showPlans && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowPlans(false)}>
          <div className="bg-white rounded-2xl w-full max-w-xl p-6 shadow-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-slate-800" style={{ fontFamily: "Outfit, sans-serif" }}>Choose a Plan</h3>
              <button onClick={() => setShowPlans(false)} className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-200 transition-colors">✕</button>
            </div>

            {/* Launch promo banner — only for monthly */}
            {planBilling === "monthly" && (
              <div className="mb-5 px-3 py-2 rounded-xl text-white text-xs font-bold flex items-center gap-2 shadow-md" style={{ background: 'linear-gradient(135deg, #F97316 0%, #D946EF 100%)' }}>
                <span className="text-base">🎉</span>
                <span>Launch Offer: 50% OFF first month — automatically applied at checkout</span>
              </div>
            )}

            {/* Toggles */}
            <div className="flex items-center justify-between mb-5">
              {/* Billing toggle */}
              <div className="flex items-center bg-slate-100 rounded-full p-0.5">
                <button onClick={() => setPlanBilling("monthly")}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${planBilling === "monthly" ? "bg-white text-slate-800 shadow-sm" : "text-slate-500"}`}>
                  Monthly
                </button>
                <button onClick={() => setPlanBilling("yearly")}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${planBilling === "yearly" ? "bg-white text-slate-800 shadow-sm" : "text-slate-500"}`}>
                  Yearly
                </button>
              </div>
              {/* Currency toggle */}
              <div className="flex items-center bg-slate-100 rounded-full p-0.5">
                <button onClick={() => setPlanCurrency("USD")}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${planCurrency === "USD" ? "bg-white text-slate-800 shadow-sm" : "text-slate-500"}`}>
                  $ USD
                </button>
                <button onClick={() => setPlanCurrency("MXN")}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${planCurrency === "MXN" ? "bg-white text-slate-800 shadow-sm" : "text-slate-500"}`}>
                  $ MXN
                </button>
              </div>
            </div>

            {/* Plan cards */}
            <div className="space-y-3">
              {plans.map(p => {
                const price = getPlanPrice(p);
                return (
                  <button key={p.key} onClick={() => handleSelectPlan(p.key)} disabled={checkoutLoading === p.key}
                    className={`w-full p-4 rounded-xl border-2 transition-all text-left ${
                      p.popular ? "border-orange-400 bg-orange-50/50 hover:border-orange-500" : "border-slate-200 bg-white hover:border-slate-300"
                    }`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-lg">{p.icon}</span>
                        <span className="text-sm font-bold text-slate-800">{p.name}</span>
                        {p.popular && <span className="text-[9px] font-bold text-orange-600 bg-orange-100 px-1.5 py-0.5 rounded-full">Most Popular</span>}
                      </div>
                      <div className="flex items-center gap-2">
                        {checkoutLoading === p.key ? (
                          <Loader2 size={16} className="animate-spin text-orange-500" />
                        ) : planBilling === "monthly" ? (
                          <>
                            <span className="text-xs text-slate-400 line-through" style={{ fontFamily: "JetBrains Mono" }}>
                              ${price.toLocaleString()}
                            </span>
                            <span className="text-base font-bold gradient-text" style={{ fontFamily: "JetBrains Mono" }}>
                              ${Math.round(price / 2).toLocaleString()}
                            </span>
                            <span className="text-[10px] text-slate-400">{planCurrency}/mo</span>
                            <ArrowRight size={14} className="text-slate-400" />
                          </>
                        ) : (
                          <>
                            <span className="text-base font-bold text-slate-800" style={{ fontFamily: "JetBrains Mono" }}>
                              ${price.toLocaleString()}
                            </span>
                            <span className="text-[10px] text-slate-400">{planCurrency}/yr</span>
                            <ArrowRight size={14} className="text-slate-400" />
                          </>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-3 ml-7">
                      <span className="text-xs text-slate-500 font-medium">{p.analyses}</span>
                      <span className="text-slate-300">·</span>
                      {p.features.map((f, i) => (
                        <span key={i} className="text-xs text-slate-400">{f}{i < p.features.length - 1 ? <span className="text-slate-300 ml-3">·</span> : ""}</span>
                      ))}
                    </div>
                    {planBilling === "monthly" && (
                      <p className="text-[10px] font-semibold mt-1.5 ml-7 gradient-text">First month 50% off · then ${price.toLocaleString()}/mo</p>
                    )}
                    {planBilling === "yearly" && (
                      <p className="text-[10px] text-emerald-500 font-semibold mt-1.5 ml-7">Save with annual billing</p>
                    )}
                  </button>
                );
              })}
            </div>

            <p className="text-[10px] text-slate-400 text-center mt-4">Cancel anytime · No hidden fees</p>
          </div>
        </div>
      )}

      {/* Stats row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={BarChart3} label="Total Analyses" value={stats?.total || 0} sub="all time" color="indigo" />
        <StatCard icon={TrendingUp} label="Average Score" value={stats?.avg_score || 0} sub="/100" color="emerald" isScore />
        <StatCard icon={Calendar} label="This Month" value={stats?.this_month || 0} sub={`of ${stats?.plan_limit || 5} limit`} color="violet" />
        <StatCard icon={Trophy} label="Top Score" value={stats?.top_ad?.results?.neuro_score || "—"} sub={stats?.top_ad?.ad_name || "No ads yet"} color="amber" isScore />
      </div>

      {/* Quick upload */}
      <div
        data-testid="quick-upload-zone"
        onClick={() => navigate("/analyze/new")}
        className="bg-white rounded-2xl border-2 border-dashed border-slate-200 hover:border-orange-300 p-8 text-center cursor-pointer transition-colors group"
      >
        <div className="w-14 h-14 rounded-2xl bg-orange-50 flex items-center justify-center mx-auto mb-4 group-hover:bg-orange-100 transition-colors">
          <Upload size={24} className="text-orange-500" />
        </div>
        <h3 className="text-base font-bold mb-1" style={{ fontFamily: "Outfit, sans-serif", color: "#0F172A" }}>
          Quick Analysis
        </h3>
        <p className="text-sm text-slate-500">Drop an ad image here or click to start a new analysis</p>
      </div>

      {/* Recent analyses */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold" style={{ fontFamily: "Outfit, sans-serif", color: "#0F172A" }}>
            Recent Analyses
          </h2>
          {analyses.length > 0 && (
            <Button variant="ghost" size="sm" onClick={() => navigate("/analyses")} className="text-orange-600 text-xs">
              View All <ArrowRight size={14} className="ml-1" />
            </Button>
          )}
        </div>

        {analyses.length === 0 ? (
          <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center">
            <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center mx-auto mb-3">
              <BarChart3 size={20} className="text-slate-400" />
            </div>
            <p className="text-sm font-medium text-slate-700 mb-1">No analyses yet</p>
            <p className="text-xs text-slate-500 mb-4">Upload your first ad to get started</p>
            <Button
              data-testid="first-analysis-btn"
              onClick={() => navigate("/analyze/new")}
              className="gradient-btn rounded-xl text-sm font-semibold"
            >
              <span>Start First Analysis</span>
            </Button>
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-100">
                  <th className="text-left text-xs font-medium text-slate-400 px-4 py-3">Ad Name</th>
                  <th className="text-left text-xs font-medium text-slate-400 px-4 py-3 hidden md:table-cell">Date</th>
                  <th className="text-left text-xs font-medium text-slate-400 px-4 py-3">Score</th>
                  <th className="text-left text-xs font-medium text-slate-400 px-4 py-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {analyses.slice(0, 8).map((a) => (
                  <tr
                    key={a.id}
                    data-testid={`analysis-row-${a.id}`}
                    onClick={() => a.status === "completed" ? navigate(`/analyze/${a.id}`) : null}
                    className={`border-b border-slate-50 ${a.status === "completed" ? "cursor-pointer hover:bg-slate-50" : ""} transition-colors`}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-orange-50 flex items-center justify-center flex-shrink-0">
                          <BarChart3 size={14} className="text-orange-500" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-slate-800">{a.ad_name}</p>
                          <p className="text-[10px] text-slate-400">{a.category} · {a.platform}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell">
                      <div className="flex items-center gap-1.5 text-xs text-slate-500">
                        <Clock size={12} />
                        {new Date(a.created_at).toLocaleDateString()}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {a.status === "completed" && a.results ? (
                        <span className={`text-lg font-bold ${scoreColor(a.results.neuro_score)}`} style={{ fontFamily: "JetBrains Mono, monospace" }}>
                          {a.results.neuro_score}
                        </span>
                      ) : (
                        <span className="text-xs text-slate-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-[10px] font-medium px-2 py-1 rounded-full border ${statusBadge(a.status)}`}>
                        {a.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Email Verification Modal */}
      {verifyModal && <VerifyEmailModal onClose={() => { setVerifyModal(false); setResent(false); }} />}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, sub, color, isScore }) {
  const colorMap = {
    indigo: "bg-orange-50 text-orange-500",
    emerald: "bg-emerald-50 text-emerald-500",
    violet: "bg-fuchsia-50 text-fuchsia-500",
    amber: "bg-amber-50 text-amber-500",
  };
  const scoreColor = isScore && typeof value === "number"
    ? (value >= 80 ? "text-emerald-600" : value >= 50 ? "text-amber-600" : "text-red-500")
    : "text-slate-900";

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5 hover:shadow-md transition-shadow">
      <div className="flex items-center gap-3 mb-3">
        <div className={`w-9 h-9 rounded-xl ${colorMap[color]} flex items-center justify-center`}>
          <Icon size={16} />
        </div>
        <span className="text-xs font-medium text-slate-500">{label}</span>
      </div>
      <div className="flex items-end gap-2">
        <span className={`text-2xl font-bold ${scoreColor}`} style={{ fontFamily: "JetBrains Mono, monospace" }}>
          {value}
        </span>
        <span className="text-xs text-slate-400 mb-1">{sub}</span>
      </div>
    </div>
  );
}
